import { CoverageScreenType } from '@components/service-and-repair/components/coverage-screen/get-logic/coverage-type';
import { SubCategory, Warranty } from '@components/service-and-repair/constants';
import { proxy } from 'valtio';

// TODO: remove state check for non-limited warranty 100-day coverage

interface SARsHighIntent {
  isHighIntent: boolean;
  experiments?: Record<string, string>;
}

interface PaintedDoor {
  assignedBucket: string;
  isTreatment: boolean;
  year: string;
  make: string;
  model: string;
  firstName: string;
  isTreatmentV1: boolean;
  isTreatmentV2: boolean;
  text?: string;
}

interface CosmeticVehicleConcernExperiment {
  assignedBucket: string;
  isTreatment: boolean;
}

export const SarStore = proxy<{
  deductibleState: string | null;
  saleEffectiveDate: Date | null;
  coverageScreenType?: CoverageScreenType;
  sarHighIntent?: SARsHighIntent;
  carvanaCareWarranty?: Warranty;
  paintedDoor: PaintedDoor;
  cosmeticVehicleConcernExperiment: CosmeticVehicleConcernExperiment;
  sarSessionId?: string;
}>({
  deductibleState: null,
  saleEffectiveDate: null,
  coverageScreenType: undefined,
  sarHighIntent: undefined,
  carvanaCareWarranty: undefined,
  paintedDoor: {} as PaintedDoor,
  cosmeticVehicleConcernExperiment: { assignedBucket: '', isTreatment: false },
  sarSessionId: undefined,
});

export const isUserWithinXDays = (days: number) => {
  if (!SarStore.saleEffectiveDate) {
    return false;
  }

  return getSalesEffectiveDate(SarStore.saleEffectiveDate, days);
};

export const getDaysSinceSale = (saleEffectiveDate: Date) => {
  const currentTime = new Date().getTime();
  const saleEffectiveTime = saleEffectiveDate.getTime();

  return Math.floor((currentTime - saleEffectiveTime) / (24 * 60 * 60 * 1000));
};

export const getSalesEffectiveDate = (saleEffectiveDate: Date, days: number) => {
  return getDaysSinceSale(saleEffectiveDate) <= days;
};

export const isUserWithinXDaysAndNOTInExcludedStates = (days: number) => {
  const excludedStates = ['CA', 'CT', 'HI', 'MA', 'MN', 'NY', 'RI'];
  const isInExcludedState = excludedStates.includes(SarStore.deductibleState?.toUpperCase() || '');
  if (isInExcludedState) {
    return false;
  }

  return isUserWithinXDays(days);
};

export const isUserWithin30DaysInCaliforniaAndLessThan1000MilesSincePurchase = () => {
  const isInCalifornia = SarStore.deductibleState?.toUpperCase() === 'CA';
  return isInCalifornia && isUserWithinXDays(30);
};

export const isCovered100DayLimitedWarrantyAndWithinXDays = (days: number) =>
  SarStore.coverageScreenType === CoverageScreenType.limited100Covered && isUserWithinXDaysAndNOTInExcludedStates(days);

export const isCovered100DayLimitedWarrantyWithin30DaysAndInCalifornia = () =>
  SarStore.coverageScreenType === CoverageScreenType.limited100Covered &&
  isUserWithin30DaysInCaliforniaAndLessThan1000MilesSincePurchase();

export const isCovered30Days = () => SarStore.coverageScreenType === CoverageScreenType.thirtyDayCovered;

export const isSubCategoryIncluded = (subcategory: SubCategory | undefined) =>
  [SubCategory.Gas, SubCategory.Tires, SubCategory.Brakes, SubCategory.Wheels].includes(subcategory as SubCategory);

export const setDeductibleState = (state: string) => {
  SarStore.deductibleState = state;
};

export const setSaleEffectiveDate = (saleEffectiveDate: Date) => {
  if (!saleEffectiveDate) {
    return;
  }

  if (typeof saleEffectiveDate === 'string') {
    saleEffectiveDate = new Date(saleEffectiveDate);
  }

  SarStore.saleEffectiveDate = saleEffectiveDate;
};

export const setCoverageScreenType = (coverageScreenType: CoverageScreenType) => {
  SarStore.coverageScreenType = coverageScreenType;
};

export const setSarHighIntent = (sarHighIntent: SARsHighIntent) => {
  SarStore.sarHighIntent = sarHighIntent;
};

export const setCarvanaCareWarranty = (warranty: Warranty) => {
  SarStore.carvanaCareWarranty = warranty;
};

export const setExperimentPaintedDoor = (assignedBucket: string) => {
  SarStore.paintedDoor.assignedBucket = assignedBucket;
  SarStore.paintedDoor.isTreatment = assignedBucket?.includes('treatment') || false;
  SarStore.paintedDoor.isTreatmentV1 = assignedBucket === 'treatment_v1';
  SarStore.paintedDoor.isTreatmentV2 = assignedBucket === 'treatment_v2';
};

export const setSarSessionId = (sessionId: string | undefined) => {
  SarStore.sarSessionId = sessionId;
};

export const setExperimentCosmeticVehicleConcern = (assignedBucket: string) => {
  SarStore.cosmeticVehicleConcernExperiment.assignedBucket = assignedBucket;
  SarStore.cosmeticVehicleConcernExperiment.isTreatment = assignedBucket?.includes('treatment') || false;
};

export const setPaintedDoorUserInfo = (year: string, make: string, model: string, firstName: string) => {
  SarStore.paintedDoor.year = year;
  SarStore.paintedDoor.make = make;
  SarStore.paintedDoor.model = model;

  // Extract first name and handle possessive forms
  // firstName can be: "John's Car", "James' Car", "O'Connors Car", etc.
  firstName = firstName?.trim().split(' ')[0] || '';

  // Remove possessive endings
  if (firstName.endsWith("'s")) {
    firstName = firstName.slice(0, -2);
  } else if (firstName.endsWith("'")) {
    firstName = firstName.slice(0, -1);
  }

  SarStore.paintedDoor.firstName = toProperCase(firstName);
};

export const setPaintedDoorText = (text: string) => {
  SarStore.paintedDoor.text = text;
};

export const toProperCase = (str: string) => {
  if (!str || str === '') return str;

  // Trim whitespace and convert to lowercase
  const trimmed = str.trim().toLowerCase();

  // Split on spaces, apostrophes, and hyphens, but keep the delimiters
  return trimmed
    .split(/(\s+|'|-)/g)
    .map((part) => {
      // Skip empty parts, spaces, apostrophes, and hyphens
      if (!part || part.match(/^\s+$/) || part === "'" || part === '-') {
        return part;
      }
      // Capitalize first letter of actual word parts
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
};
